import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import { api, ifMatch } from "@/services/api";
import type {
  DashboardSummary,
  EffectivePolicy,
  Group,
  GroupSummary,
  Page,
  Permission,
  Profile,
  Session,
  TokenPair,
} from "@/meta/types";

export const queryKeys = {
  profile: ["profile"] as const,
  sessions: ["sessions"] as const,
  groups: (params: GroupListParams) => ["groups", params] as const,
  group: (id: number) => ["groups", id] as const,
  permissions: (params: PermissionListParams) => ["permissions", params] as const,
  contentTypes: ["content-types"] as const,
  // Under the same prefix the policy screens invalidate, so editing a policy
  // refreshes the resolved copy the shell is holding.
  effectivePolicy: ["/access-policies", "effective", "me"] as const,
};

export interface GroupListParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: "name" | "created_at" | "id";
  order?: "asc" | "desc";
}

export interface PermissionListParams {
  page?: number;
  size?: number;
  search?: string;
  content_type_id?: number;
  sort?: "codename" | "name" | "id";
  order?: "asc" | "desc";
}

export const profileQuery = () =>
  queryOptions({
    queryKey: queryKeys.profile,
    queryFn: async ({ signal }) => (await api.get<Profile>("/me", { signal })).data,
    staleTime: 60_000,
  });

export const sessionsQuery = () =>
  queryOptions({
    queryKey: queryKeys.sessions,
    queryFn: async ({ signal }) =>
      (await api.get<Session[]>("/auth/sessions", { signal })).data,
  });

export const groupsQuery = (params: GroupListParams) =>
  queryOptions({
    queryKey: queryKeys.groups(params),
    queryFn: async ({ signal }) =>
      (await api.get<Page<GroupSummary>>("/groups", { params, signal })).data,
    placeholderData: (previous) => previous,
  });

export const groupQuery = (id: number) =>
  queryOptions({
    queryKey: queryKeys.group(id),
    queryFn: async ({ signal }) => (await api.get<Group>(`/groups/${id}`, { signal })).data,
  });

export const permissionsQuery = (params: PermissionListParams) =>
  queryOptions({
    queryKey: queryKeys.permissions(params),
    queryFn: async ({ signal }) =>
      (await api.get<Page<Permission>>("/permissions", { params, signal })).data,
    placeholderData: (previous) => previous,
  });

/**
 * The caller's own policy, already resolved — user → group → organization →
 * app default — with `sources` saying where each value came from.
 *
 * Loaded once for the signed-in app. It changes about as often as the
 * organization does, and a deployment that does not serve it is not worth
 * retrying on every mount.
 */
/**
 * Every figure the dashboard shows, in one call.
 *
 * Sections the caller may not see come back null, so the screen draws a closed
 * door rather than a hole. Held for a minute: these are counts over the whole
 * table, and nobody watches them tick.
 */
export const dashboardSummaryQuery = () =>
  queryOptions({
    queryKey: ["/dashboard", "summary"] as const,
    queryFn: async ({ signal }) =>
      (await api.get<DashboardSummary>("/dashboard/summary", { signal })).data,
    staleTime: 60_000,
    retry: false,
  });

export const effectivePolicyQuery = () =>
  queryOptions({
    queryKey: queryKeys.effectivePolicy,
    queryFn: async ({ signal }) =>
      (await api.get<EffectivePolicy>("/access-policies/effective/me", { signal })).data,
    staleTime: 5 * 60_000,
    retry: false,
  });

/** What a person may change about their own account. Everything is optional:
 *  the sheet sends the field it touched, and `''` clears the ones that can be. */
export interface ProfileUpdateInput {
  first_name?: string;
  middle_name?: string | null;
  last_name?: string;
  email_address?: string;
  /** A data URL; `''` removes the picture. */
  avatar?: string;
  /** Up to 500 characters; `''` clears it. */
  notes?: string;
  /** Whole, dialling code included; `''` clears it. */
  phone?: string;
  /** ISO 3166-1 alpha-2, for the flag alone. */
  phone_country?: string;
  /** The whole list, replacing what is on record; `[]` clears it. */
  contacts?: Array<{ phone: string; country?: string | null }>;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  /**
   * The precondition `PATCH /me` refuses to write without — 428 otherwise.
   *
   * Normally the cached profile, which is the record the sheet was filled in
   * from. It is fetched when the cache has none rather than sent empty: a
   * screen can reach this before `/me` has landed, and the alternative is a
   * 428 telling the person to send a header they have never heard of. One
   * request, only on the path that would otherwise have failed.
   */
  const precondition = async () => {
    const cached = queryClient.getQueryData<Profile>(queryKeys.profile);
    if (cached?.updated_at) return ifMatch(cached);
    const fresh = await queryClient.fetchQuery(profileQuery());
    return ifMatch(fresh);
  };

  return useMutation({
    mutationFn: async (input: ProfileUpdateInput) =>
      (await api.patch<Profile>("/me", input, await precondition())).data,
    onSuccess: (profile) => queryClient.setQueryData(queryKeys.profile, profile),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: { current_password: string; new_password: string }) =>
      (await api.post<TokenPair>("/me/password", input)).data,
  });
}

export function useRequestPasswordReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reason?: string }) =>
      (await api.post<{ message: string }>("/me/password-request", input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.profile }),
  });
}

export function useUpdateGroupPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, codenames }: { id: number; codenames: string[] }) =>
      (await api.patch<Group>(`/groups/${id}`, { codenames })).data,
    onSuccess: (group) => {
      queryClient.setQueryData(queryKeys.group(group.id), group);
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}
